import styled from 'styled-components'
import { tokens } from 'styling/tokens'
import { IStatusPill } from 'types/ui/statusPill.interface'

export const TanksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  @media (max-width: 87.5rem) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 68.75rem) {
    grid-template-columns: repeat(2, 1fr);
  }
`

export const TankCard = styled.div`
  background: ${tokens.surface.inset};
  border: 1px solid ${tokens.border.hairline};
  border-radius: ${tokens.radius.card};
  box-shadow: ${tokens.shadow.card};
  padding: 1rem;
  display: flex;
  gap: 1rem;
  align-items: stretch;
`

export const TankInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`

export const TankTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
`

export const TankName = styled.span`
  font-size: ${tokens.font.body};
  font-weight: 600;
  color: ${tokens.text.primary};
`

export const TankMetricLabel = styled.div`
  font-size: ${tokens.font.micro};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.text.muted};
  margin-bottom: 0.125rem;
`

export const TankMetricValue = styled.div<{ $dim?: boolean }>`
  font-size: ${tokens.font.micro};
  font-weight: 600;
  color: ${({ $dim }) => ($dim ? tokens.text.muted : tokens.text.secondary)};
  font-variant-numeric: tabular-nums;
`

export const TankMetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`

export const CoverValue = styled.div<{ $tone: IStatusPill['status']; $dim?: boolean }>`
  font-size: ${tokens.font.micro};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone, $dim }) =>
    $dim
      ? tokens.text.muted
      : $tone === 'danger' || $tone === 'warning'
        ? tokens.status[$tone].fg
        : tokens.text.secondary};
`

export const CardNote = styled.p`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
  line-height: 1.4;
`

export const WaterNote = styled.p<{ $warning?: boolean }>`
  font-size: ${tokens.font.micro};
  font-weight: 600;
  line-height: 1.4;
  color: ${({ $warning }) => ($warning ? tokens.status.warning.fg : tokens.text.secondary)};
`

export const LedgerNote = styled.span`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
`

export const BreachNoteText = styled.p`
  font-size: ${tokens.font.micro};
  font-weight: 600;
  color: ${tokens.status.danger.fg};
`

export const TotalsLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${tokens.border.hairline};
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.inset};
  font-size: ${tokens.font.body};
  font-weight: 600;
  color: ${tokens.text.primary};
  font-variant-numeric: tabular-nums;
`

export const FootnoteLine = styled.p`
  margin-top: 1rem;
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
`

export const LedgerWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-width: 34rem;
`

export const LedgerRow = styled.div<{ $em?: boolean }>`
  display: grid;
  grid-template-columns: 1.25rem 1fr auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${tokens.radius.chip};
  ${({ $em }) => $em && `background: ${tokens.surface.inset}; border: 1px solid ${tokens.border.hairline};`}
`

export const LedgerOp = styled.span`
  font-size: ${tokens.font.body};
  font-weight: 700;
  color: ${tokens.text.muted};
  text-align: center;
`

export const LedgerLabel = styled.span`
  font-size: ${tokens.font.body};
  color: ${tokens.text.secondary};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`

export const LedgerValue = styled.span<{ $em?: boolean }>`
  font-size: ${tokens.font.body};
  font-weight: ${({ $em }) => ($em ? 700 : 600)};
  color: ${tokens.text.primary};
  font-variant-numeric: tabular-nums;
`

export const VarianceRow = styled.div<{ $tone: 'success' | 'danger' }>`
  margin-top: 0.25rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${tokens.radius.chip};
  background: ${({ $tone }) => tokens.status[$tone].bg};
  color: ${({ $tone }) => tokens.status[$tone].fg};
  font-size: ${tokens.font.body};
  font-weight: 700;
`

export const LinkText = styled.span`
  font-size: ${tokens.font.label};
  font-weight: 600;
  color: ${tokens.status.info.fg};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

export const SignOffWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px dashed ${tokens.border.hairline};
  max-width: 34rem;
`

export const SignedText = styled.span`
  font-size: ${tokens.font.body};
  font-weight: 600;
  color: ${tokens.status.success.fg};
`
